// pageConfig.js
import React from "react";
import {
  Scale,
  Zap,
  Rocket,
  ChevronsUp,
  Calculator,
  Repeat,
  Star,
  AlertTriangle,
  ZoomIn,
  Combine,
  Plus,
  TrendingUp,
  Ban,
  GalleryHorizontalEnd,
  BadgePoundSterling
} from "lucide-react";
import Collapsible from "../Components/ui/Collapsible.jsx";

import OddsBoostCalculator from "../Components/Calculators/OddsBoostCalculator.jsx";
import FractionalToDecimalConverter from "../Components/Calculators/FractionalToDecimalConverter.jsx";
import DecimalToFractionalConverter from "../Components/Calculators/DecimalToFractionalConverter.jsx";
import OddsConverter from "../Components/Calculators/OddsConverter.jsx";
import RiskFreeEBOCalculator from "../Components/Calculators/RiskFreeEBOCalculator.jsx";
import PartialLayCalculator from "../Components/Calculators/PartialLayCalculator.jsx";
import EnhancedOddsCalculator from "../Components/Calculators/EnhancedOddsCalculator.jsx";
import AccaPickerCalculator from "../Components/Calculators/AccaPickerCalculator.jsx";
import MatchPickerCalculator from "../Components/Calculators/MatchPickerCalculator.jsx";
import ExtraPlaceMatcherCalculator from "../Components/Calculators/ExtraPlaceMatcherCalculator.jsx";
import OddsBoostCalculatorAdv from "../Components/Calculators/OddsBoostCalculatorAdv.jsx";
import LayStakeCalculator from "../Components/Calculators/LayStakeCalculator.jsx";
import FreeBetCalculator from "../Components/Calculators/FreeBetCalculator.jsx";


import RiskFreeCalculator from "../Components/Calculators/RiskFreeCalculator.jsx";
import UnwantedLayCalculator from "../Components/Calculators/UnwantedLayCalculator.jsx";
import ArbCalculator from "../Components/Calculators/ArbCalculator.jsx";
import DutchingCalculator from "../Components/Calculators/DutchingCalculator.jsx";
import RefundCalculator from "../Components/Calculators/RefundCalculator.jsx";
import { GiHorseHead } from "react-icons/gi";
const createInstructions = (jsx) => jsx;

const pageConfig = {
  layStakeCalculator: {
    route: "/calculators/lay-stake-calculator",
    component: LayStakeCalculator,
    seo: {
      title: "Lay Stake Calculator | Matched Betting Calculators",
      description:
        "Find the optimum lay stake and potential profit for your matched bets. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Lay Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Scale size={20} />,
      label: "Lay Stake",
      sub: "",
    },
    fullTitle: "Lay Stake Calculator",
    brief: "Calculate your optimal lay stake and lock in profit from matched betting.",
    similar: ["riskFreeBetCalculator", "matchPickerCalculator", "accaPickerCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "matchedBettingGuide",
    instructions: createInstructions(
      <div>
        <p>Find the optimum lay stake and profit from your matched bets.</p>
      </div>
    ),
  },

  freeBetCalculator: {
    route: "/calculators/free-bet-calculator",
    component: FreeBetCalculator,
    seo: {
      title: "Free Bet Calculator | Matched Betting Calculators",
      description:
        "Find the optimum lay stake and potential profit for your matched bets. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Free Bet Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <BadgePoundSterling size={20} />,
      label: "Free Bet",
      sub: "",
    },
    fullTitle: "Free Bet Calculator",
    brief: "Calculate your optimal free bet lay stake and lock in profit.",
    similar: ["riskFreeBetCalculator", "matchPickerCalculator", "accaPickerCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "FreeBetsGuide",
    instructions: createInstructions(
      <div>
        <p>Find the optimum lay stake and profit from your matched bets.</p>
      </div>
    ),
  },

  advancedOddsBoostCalculator: {
    route: "/calculators/boost-calculator-advanced",
    component: OddsBoostCalculatorAdv,
    seo: {
      title:
        "Lay Stake Calculator - Boosted Odds | Matched Betting Calculators",
      description:
        "Calculate optimum lay stakes and expected profit from boosted odds. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Boosted Lay Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Zap size={20} />,
      label: "Lay Stake",
      sub: "With Boost",
    },
    keywords: {
      primary: ["profit boost", "odds boost", "boost calculator"]
    },
    fullTitle: "Boosted Lay Calculator",
    brief: "Get your lay stake and expected profit from boosted odds bets.",
    similar: ["layStakeCalculator","riskFreeBetCalculator", "matchPickerCalculator"],
    similarGuides: ["HowToReadOddsGuide","matchedBettingGuide","FreeBetsGuide"],
    relatedGuide: "FreeBetsGuide",
    instructions: createInstructions(
      <div>
        <p>Get your optimum lay stake and expected profit from boosted odds bets.</p>
      </div>
    ),
  },

  riskFreeBetCalculator: {
    route: "/calculators/risk-free-calculator",
    component: RiskFreeCalculator,
    seo: {
      title: "Risk-Free Bet Calculator | Matched Betting Calculators",
      description:
        "Calculate your lay stake and potential profit from risk free bet offers. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Risk-Free Bet Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Star size={20} />,
      label: "Risk-Free Bet",
      sub: "",
    },
    fullTitle: "Risk-Free Bet Calculator",
    brief: "Work out profit and lay stake for risk-free bet offers.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "freeBetCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "RiskFreeBetOffersGuide",

    instructions: createInstructions(
      <div>
        <p>
          Calculate your lay stake and potential profit from risk-free bet offers.
        </p>
        <p>
          Free bet retention is how much of that bet you can lock in if it loses.
        </p>
      </div>
    ),
  },

  partialLayCalculator: {
    route: "/calculators/partial-lay",
    component: PartialLayCalculator,
    seo: {
      title: "Partial Lay Calculator | Matched Betting Calculators",
      description:
        "Determine the optimal additional lay stake when liquidity is limited. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Partial Lay Calculator",
      matched: true,
      advantage: false,
      experimental: true,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Calculator size={20} />,
      label: "Partial Lay",
      sub: "",
    },
    keywords: {
      primary: ["incomplete lay", "unfinished lay"],
      secondary: ["cancelled lay"]
    },
    fullTitle: "Partial Lay Calculator",
    brief: "Adjust your lay bet when liquidity is limited across multiple odds.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "riskFreeBetCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],

    instructions: createInstructions(
      <div>
        <p>
          Sometimes we need to lay our back bets at multiple odds, because there's limited lay liquidity.
        </p>
        <p>
          This calculator helps you work out how much more to lay at the available odds.
        </p>
      </div>
    ),
  },

  arbCalculator: {
    route: "/calculators/arb-calculator",
    component: ArbCalculator,
    seo: {
      title: "Arb Calculator | Matched Betting Calculators",
      description:
        "Calculate arbitrage opportunities and find guaranteed profit scenarios in matched betting. Free online matched betting calculators.",
    },
    use: {
      title: "Arb Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <TrendingUp size={20} />,
      label: "Arbitrage",
      sub: "",
    },
    keywords: {
      primary: ["arb calculator", "arbitrage calculator"],
      secondary: []
    },
    fullTitle: "Arbitrage Calculator",
    brief: "Spot and calculate guaranteed profits from arbitrage betting opportunities.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "riskFreeBetCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],

    instructions: createInstructions(
      <div>
        <p>
          Use the Arb Calculator to compute arbitrage opportunities and secure risk-free profits from your matched bets.
        </p>
      </div>
    ),
  },

  matchPickerCalculator: {
    route: "/calculators/match-picker",
    component: MatchPickerCalculator,
    seo: {
      title: "Match Picker | Matched Betting Calculators",
      description:
        "Compare lay options quickly when you don't have a matcher available. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Match Picker",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <ZoomIn size={20} />,
      label: "Match Picker",
      sub: "",
    },
    keywords: {
      primary: ["odds matcher", "odds finder", "odds picker"],
      secondary: ["bet matcher", "find bets", "find matches"]
    },
    fullTitle: "Match Picker",
    brief: "Find the best lay match when no matcher is available.",
    similar: ["accaPickerCalculator", "layStakeCalculator", "riskFreeBetCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "OddsMonkeyGuideSimple",

    instructions: createInstructions(
      <div>
        <p>
          Compare lay options quickly when you don't have a matcher available. The picker will highlight the best match available.
        </p>
      </div>
    ),
  },

  accaPickerCalculator: {
    route: "/calculators/acca-picker",
    component: AccaPickerCalculator,
    seo: {
      title: "Acca Picker | Matched Betting Calculators",
      description:
        "Compare acca options quickly when you don't have a matcher available. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Acca Picker",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Combine size={20} />,
      label: "Acca Picker",
      sub: "",
    },
    fullTitle: "Acca Picker",
    keywords: {
      primary: ["how to lay accumulator", "how to find accumulator selections", "accumulator matches"],
      secondary: []
    },
    brief: "Compare outcomes and build accumulator bets for matched betting.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "riskFreeBetCalculator"],
    similarGuides: ["AccaBetsGuide","LayAccaSmarketsGuide","LayStakeGuide"],
    relatedGuide: "AccaBetsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Find the best combination for matched betting accas quickly. The picker will highlight the best combo available that meets the minimum odds requirements.
        </p>
        <p>
          Add single outcomes (ideal for golf or horses), 2-way events (like tennis), or 3-way events (such as football).
        </p>
        <p>
          The picker will only select <strong>one outcome per event</strong> so you can find valid accas.
        </p>
      </div>
    ),
  },

  oddsBoostCalculator: {
    route: "/calculators/boost-calculator",
    component: OddsBoostCalculator,
    seo: {
      title: "Odds Boost Calculator | Matched Betting Calculators",
      description:
        "Easily calculate boosted odds by entering decimal odds and a boost percentage. For profit boost and odds boost offers.",
    },
    use: {
      title: "Odds Boost Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Advantage",
      icon: <Rocket size={20} />,
      label: "Odds Boost",
      sub: "Simple",
    },
    fullTitle: "Odds Boost Calculator",
    keywords: {
      primary: ["profit boost", "boost calculator", "odds boost"],
      secondary: []
    },
    brief: "Convert boost percentage into improved decimal odds instantly.",
    similar: ["advancedOddsBoostCalculator", "layStakeCalculator", "matchPickerCalculator"],

    similarGuides: ["matchedBettingGuide","HowToReadOddsGuide","FreeBetsGuide"],
    relatedGuide: "PriceBoostsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Enter decimal odds and a boost percentage to see the improved odds instantly.
        </p>
      </div>
    ),
  },

  riskFreeEBOCalculator: {
    route: "/calculators/risk-free-ebo",
    component: RiskFreeEBOCalculator,
    seo: {
      title: "Risk-Free EBO Calculator | Matched Betting Calculators",
      description:
        "Assess risk-free bets with Equivalent Boosted Odds with this Risk-Free Bet Calculator. Accurately assess potential profit and loss to make informed matched betting decisions.",
    },
    use: {
      title: "Risk-Free Advantage Play Calculator and Equivalent Boosted Odds",
      matched: false,
      advantage: true,
      experimental: true,
      copyable: false,
    },
    sidebar: {
      category: "Advantage",
      icon: <AlertTriangle size={20} />,
      label: "AP",
      sub: "Risk-Free",
    },
    keywords: {
      primary: ["advantage play", "risk-free bet"],
      secondary: []
    },
    fullTitle: "Risk-Free Advantage Play Calculator",
    brief: "Analyse risk-free bets using Equivalent Boosted Odds (EBO) logic.",
    similar: ["riskFreeBetCalculator", "LayStakeCalculator", "ExtraPlaceMatcherCalculator"],
    similarGuides: ["EquivalentBoostedOddsGuide","RiskFreeBetOffersGuide","LayBetBuildersGuide"],
    relatedGuide: "EquivalentBoostedOddsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Equivalent Boosted Odds (EBO) reframes risk‐free bet offers as value bets by converting the risk-free aspect of the bet into an odds boost.
        </p>
        <p>
          In other words, treating a risk-free bet offer as Advantage Play is equivalent to punting a bet at greatly improved odds.
        </p>
        <p>
          Use this calculator to decide whether to AP, lay it if possible, or skip.
        </p>
      </div>
    ),
  },

  enhancedOddsCalculator: {
    route: "/calculators/enhanced-odds",
    component: EnhancedOddsCalculator,
    seo: {
      title: "Enhanced Odds Calculator | Matched Betting Calculators",
      description:
        "Use this calculator to work out the original odds when bookies offer enhanced winnings. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Enhanced Odds Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Advantage",
      icon: <ChevronsUp size={20} />,
      label: "Enhanced Odds",
      sub: "",
    },
    keywords: {
      primary: ["profit boost", "boost calculator", "odds boost"],
      secondary: []
    },
    fullTitle: "Enhanced Odds Calculator",
    brief: "Work out original odds behind enhanced winnings offers.",
    similar: ["layStakeCalculator", "oddsBoostCalculator", "riskFreeBetCalculator"],

    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "PriceBoostsGuide",

    instructions: createInstructions(
      <div>
        <p>
          When bookies offer enhanced winnings, sometimes they don't tell you the new odds.
        </p>
        <p>
          Use this calculator to work out the original odds.
        </p>
      </div>
    ),
  },

  extraPlaceMatcherCalculator: {
    route: "/calculators/extra-place",
    component: ExtraPlaceMatcherCalculator,
    seo: {
      title: "Extra Place Calculator | Matched Betting Calculators",
      description:
        "Assess potential profitability of matched betting on extra place offers. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Extra Place Analyser",
      matched: false,
      advantage: true,
      experimental: true,
      copyable: false,
    },
    sidebar: {
      category: "Advantage",
      icon: <Plus size={20} />,
      label: "Extra Place",
      sub: "Analyser",
    },
    keywords: {
      primary: ["each way", "E/W", "horse racing", "golf"],
      secondary: []
    },
    fullTitle: "Extra Place Analyser",
    brief: "Assess value in extra place matched betting opportunities.",
    similar: ["RefundCalculator","riskFreeEBOCalculator", "riskFreeBetCalculator"],

    similarGuides: ["ExtraPlaceGuide","horseRefundGuide","LayBetBuildersGuide"],
    relatedGuide: "ExtraPlaceGuide",

    instructions: createInstructions(
      <div>
        <p>
          Assess the risk vs. reward and theoretical profitability of extra place matched betting.
        </p>
        <p>
          When we matched bet extra place offers, we accept a small potential loss on our lay bet for the chance to make a profit if our selection lands in an extra place.
        </p>
        <p>
          Use this calculator to assess potential profitability by weighing that small loss against the profit opportunity.
        </p>
        <div className="metric-explainer">
  <Collapsible title="Risk : Reward Ratio">
    <p>
      <code>1:X</code> means you risk £1 to potentially win £X.
    </p>
  </Collapsible>

  <Collapsible title="Implied Odds">
    <p>A measure of how potential profit compares to qualifying loss.</p>
    <p>
      <code>(potential profit ÷ qualifying loss) + 1</code>
    </p>
    <p><em>Example:</em> If you could win £20 for a £1 risk, implied odds = (20 ÷ 1) + 1 = 21.00.</p>
  </Collapsible>

  <Collapsible title="EV Odds">
    <p>
      The average odds you’re effectively playing at once you factor in the chance of landing the extra place.
    </p>
    <p>
      Weights the true odds by the refund probability.
    </p>
    <p><em>Example:</em> A 60% chance at 3.0 odds gives EV odds of 1.5 (0.6×3.0 + 0.4×1.0).</p>
  </Collapsible>

  <Collapsible title="Expected Value">
    <p>
      Your long‑term average profit (or loss) per unit staked.
    </p>
    <p>A £10 stake returning £4 on average gives EV = £4 (40%).</p>
  </Collapsible>
</div>
      </div>
    ),
  },

  oddsConverter: {
    route: "/calculators/odds-converter",
    component: OddsConverter,
    seo: {
      title: "Odds Converter | Matched Betting Calculators",
      description:
        "Easily convert between decimal, fractional, and American odds. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Odds Converter",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Converters",
      icon: <Repeat size={20} />,
      label: "Odds Converter",
      sub: "",
    },
    fullTitle: "Odds Converter",
    brief: "Convert between decimal, fractional, and American odds instantly.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "riskFreeBetCalculator"
    ],
    similarGuides: ["matchedBettingGuide","HowToReadOddsGuide","LayStakeGuide"],
    relatedGuide: "HowToReadOddsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Convert instantly between decimal, fractional and American odds.
        </p>
      </div>
    ),
  },

  fractionalToDecimalConverter: {
    route: "/calculators/fractional-to-decimal",
    component: FractionalToDecimalConverter,
    seo: {
      title:
        "Fractional to Decimal Converter | Matched Betting Calculators",
      description:
        "Quickly convert fractional odds into decimal odds. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Fractional to Decimal Converter",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Converters",
      icon: <Repeat size={20} />,
      label: "Fractional",
      sub: "to Decimal",
    },
    fullTitle: "Fractional to Decimal Converter",
    brief: "Quickly convert fractional odds to decimal format.",
    similar: ["oddsConverter", "layStakeCalculator", "matchPickerCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "HowToReadOddsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Enter fractional odds (e.g. 5/2) to instantly see their decimal form.
        </p>
      </div>
    ),
  },

  decimalToFractionConverter: {
    route: "/calculators/decimal-to-fractional",
    component: DecimalToFractionalConverter,
    seo: {
      title:
        "Decimal to Fractional Converter | Matched Betting Calculators",
      description:
        "Instantly convert decimal odds into fractional odds. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Decimal to Fractional Converter",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Converters",
      icon: <Repeat size={20} />,
      label: "Decimal",
      sub: "to Fractional",
    },
    fullTitle: "Decimal to Fractional Converter",
    brief: "Quickly convert decimal odds to fractional format.",
    similar: ["oddsConverter", "layStakeCalculator", "matchPickerCalculator"],
    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],
    relatedGuide: "HowToReadOddsGuide",

    instructions: createInstructions(
      <div>
        <p>
          Enter decimal odds (e.g. 3.5) to instantly see their fractional form.
        </p>
      </div>
    ),
  },

  dutchingCalculator: {
    route: "/calculators/dutching-calculator",
    component: DutchingCalculator,
    seo: {
      title: "Dutching Calculator | Matched Betting Calculators",
      description:
        "Calculate optimal stake distribution for a fixed target profit or liability across multiple outcomes with the Dutching Calculator.",
    },
    use: {
      title: "Dutching Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: false,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <GalleryHorizontalEnd size={20} />,
      label: "Dutch",
      sub: "",
    },
    fullTitle: "Dutching Calculator",
    brief: "Split stakes across outcomes to guarantee fixed profit or liability.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "accaPickerCalculator"],

    similarGuides: ["dutchingGuide","ExtraPlaceGuide","LayBetBuildersGuide"],

    instructions: createInstructions(
      <div>
        <p>
          Use the Dutching Calculator to distribute your stake across multiple outcomes to lock in a profit.
        </p>
        <p>
          Enter your target total stake or target stake for the first outcome and the odds for each outcome.
        </p>
        <p>
          Calculate the optimal stake for each outcome along with the potential returns and profits.
        </p>
      </div>
    ),
  },

  unwantedLayCalculator: {
    route: "/calculators/unwanted-lay-calculator",
    component: UnwantedLayCalculator,
    seo: {
      title: "Unwanted Lay Calculator | Matched Betting Calculators",
      description:
        "Use this free calculator to find the back stake required to neutralise your unwanted lay bet. Calculate the back stake to minimise your losses.",
    },
    use: {
      title: "Unwanted Lay Calculator",
      matched: true,
      advantage: false,
      experimental: false,
      copyable: true,
    },
    sidebar: {
      category: "Lay Calculators",
      icon: <Ban size={20} />,
      label: "Unwanted Lay",
      sub: "",
    },
    keywords: {
      primary: ["cancel lay", "undo lay", "cancel bet"],
      secondary: []
    },
    fullTitle: "Unwanted Lay Calculator",
    brief: "Minimise losses by adjusting an unwanted or mistaken lay bet.",
    similar: ["layStakeCalculator", "matchPickerCalculator", "accaPickerCalculator"],

    similarGuides: ["matchedBettingGuide","FreeBetsGuide","LayStakeGuide"],

    instructions: createInstructions(
      <div>
        <p>
          Use this calculator to find the back stake required to neutralise your unwanted lay bet.
        </p>
        <p>
          You can either enter the unwanted lay amount directly or calculate it from your total lay bet and intended lay bet.
        </p>
        <Collapsible title="When to use this">
          <p>This tool is for when you have placed a lay bet that you do not want to keep.</p>
          <p>You don't want to take the risk leaving the position open – doing so is just gambling, and you could make a huge loss (your liability).</p>
        </Collapsible>
        <Collapsible title="Will I make a loss?">
          <p>Usually, cancelling out an unwanted lay bet will result in a small loss (in the same way that matching a back bet often results in a small loss). Sometimes, you might make a profit.</p>
          <p>The calculator shows you exactly how much profit or loss you will make.</p>
        </Collapsible>
        <Collapsible title="Which exchanges can I use?">
          <p>This method works with all exchanges, including Smarkets, Betfair, Matchbook and BETDAQ. You can also place the back bet with a bookie.</p>
        </Collapsible>
      </div>
    ),
  },

  refundCalculator: {
    route: "/calculators/refund-money-back",
    component: RefundCalculator,
    seo: {
      title: "Horse Racing Refunds Calculator | Matched Betting Calculators",
      description:
        "Assess potential profitability of matched betting on money back offers. Free online matched betting calculators for locking in profit.",
    },
    use: {
      title: "Horse Racing Refunds Calculator",
      matched: false,
      advantage: true,
      experimental: true,
      copyable: false,
    },
    sidebar: {
      category: "Advantage",
      icon: <GiHorseHead  size={20}/>,
      label: "Racing Refunds",
      sub: "",
    },
    keywords: {
      primary: ["money back", "cashback", "horse racing", "golf", "tennis"],
      secondary: ["free bet if"]
    },
    fullTitle: "Horse Racing Refunds Calculator",
    brief: "Assess value in horse racing refunds matched betting opportunities.",
    similar: ["ExtraPlaceMatcherCalculator", "RiskFreeEBOCalculator", "riskFreeBetCalculator"],

    similarGuides: ["HorseRefundGuide","ExtraPlaceGuide","LayBetBuildersGuide"],
    relatedGuide: "horseRefundGuide",

    instructions: createInstructions(
      <div>
      <p>
        Use this calculator to evaluate horse‑racing “money back” offers, where you hedge your back bet with a lay bet and hope your selection finishes in the refunded places.
      </p>
      <div className="metric-explainer">
  <Collapsible title="Risk : Reward Ratio">
    <p>
      <code>1:X</code> means you risk £1 to potentially win £X.
    </p>
  </Collapsible>

  <Collapsible title="Implied Odds">
    <p>A measure of how potential profit compares to qualifying loss.</p>
    <p>
      <code>(potential profit ÷ qualifying loss) + 1</code>
    </p>
    <p><em>Example:</em> If you could win £20 for a £1 risk, implied odds = (20 ÷ 1) + 1 = 21.00.</p>
  </Collapsible>

  <Collapsible title="EV Odds">
    <p>
      The average odds you’re effectively playing at once you factor in the chance of getting a refund.
    </p>
    <p>
      Weights the true odds by the refund probability.
    </p>
    <p><em>Example:</em> A 60% chance at 3.0 odds gives EV odds of 1.5 (0.6×3.0 + 0.4×1.0).</p>
  </Collapsible>

  <Collapsible title="Expected Value">
    <p>
      Your long‑term average profit (or loss) per unit staked.
    </p>
    <p>A £10 stake returning £4 on average gives EV = £4 (40%).</p>
  </Collapsible>
</div>

    </div>
    ),
  },
};


export default pageConfig;
